import { Box, Container, CssBaseline, Stack, Toolbar } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import UploadFileDialog from '../Components/Dialogs/Project/UploadFileDialog';
import FileDropOverlay from '../Components/Projects/FileDropOverlay';
import ProjectControls from '../Components/Projects/ProjectControls';
import ProjectList from '../Components/Projects/ProjectList';
import ProjectsHeader from '../Components/Projects/ProjectsHeader';
import BasicSnackBar from '../Components/Snackbars/BasicSnackBar';

const ProjectsPage = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [openUploadDialog, setOpenUploadDialog] = useState(false);

  const [isDragging, setIsDragging] = useState(false);
  const [isMultipleFile, setIsMultipleFile] = useState(false);
  const [draggedFiles, setDraggedFile] = useState<File | File[] | undefined>();

  const validFiles = useRef<File[]>([]);

  const [openInvalidFileSnackBar, setInvalidFileSnackBar] = useState(false);

  const handleOpenUploadDialog = () => {
    setOpenUploadDialog(true);
  };

  const handleCloseUploadDialog = () => {
    setOpenUploadDialog(false);
  };

  const handleCloseInvalidFileSnackBar = () => {
    setInvalidFileSnackBar(false);
  };

  const handleOpenInvalidFileSnackBar = () => {
    setInvalidFileSnackBar(true);
  };

  useEffect(() => {
    const handleDragOver = (e: DragEvent) => {
      e.preventDefault();
      setIsDragging(true);
    };

    const handleDragLeave = () => {
      setIsDragging(false);
    };

    const handleDrop = (e: DragEvent) => {
      if (!e.dataTransfer) return;
      e.preventDefault();
      setIsDragging(false);

      const { files } = e.dataTransfer;

      validFiles.current = [];

      Array.from(files).forEach((file: File) => {
        if (file && file.name.endsWith('.bps')) {
          validFiles.current.push(file);
        }
      });

      if (validFiles.current.length === 1) {
        setIsMultipleFile(false);
        setDraggedFile(validFiles.current.at(0));
        handleOpenUploadDialog();
      } else if (validFiles.current.length > 1) {
        setIsMultipleFile(true);
        setDraggedFile(validFiles.current);

        handleOpenUploadDialog();
      } else {
        handleOpenInvalidFileSnackBar();
      }
    };

    window.addEventListener('dragover', handleDragOver);
    window.addEventListener('dragleave', handleDragLeave);
    window.addEventListener('drop', handleDrop);

    return () => {
      window.removeEventListener('dragover', handleDragOver);
      window.removeEventListener('dragleave', handleDragLeave);
      window.removeEventListener('drop', handleDrop);
    };
  }, [dispatch]);

  return (
    <>
      <CssBaseline />
      <Container
        sx={{
          height: 'calc(100vh - 50px)',
          padding: '24px',
          margin: '0',
          flexGrow: 1,
          overflowY: 'auto',
          width: '100%',
        }}
        maxWidth={false}
      >
        <Box>
          <Stack spacing={1}>
            <Toolbar />
            <ProjectsHeader />
            <ProjectControls />
            <ProjectList />
          </Stack>
          {isDragging && <FileDropOverlay />}
        </Box>
      </Container>
      {openUploadDialog && (
        <UploadFileDialog
          open={openUploadDialog}
          onClose={handleCloseUploadDialog}
          draggedFiles={draggedFiles ? draggedFiles : []}
          isMultipleFile={isMultipleFile}
        />
      )}
      {openInvalidFileSnackBar && (
        <BasicSnackBar
          message={t('uploadFileDialog.invalidFileError', 'Invalid file type. Please select a .bps file.')}
          vertical="bottom"
          horizontal="center"
          open={openInvalidFileSnackBar}
          onClose={handleCloseInvalidFileSnackBar}
        />
      )}
    </>
  );
};

export default ProjectsPage;
